import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { fetchInsightsSearch } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { ADMIN_BASE } from '../../../utils/hosts';
import { SIGNAL_RULES } from '../../../core/rules/insights/signals';
import { ChartFrame } from '../charts/ChartFrame';
import { Kpi } from '../Kpi';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { fmt } from '../insightFormat';
import type { PeriodState } from '../../sales/usePeriodParams';
const ctr = (clicks: number, shown: number) => shown > 0 ? Math.round(clicks / shown * 1000) / 10 : null;

/** Gevonden in Google (Search Console): zoekwoorden, pagina's, stijgers en dalers. */
export function SearchBlock({
  period
}: {
  period: PeriodState;
}) {
  const report = useReport(fetchInsightsSearch, period.from, period.to);
  return <ReportView report={report} what="De cijfers uit Google" integration="searchConsole" data-id="element-1934" data-mp="1fb30wlt1g6vy">
      {s => {
      const weak = s.pages.filter(p => p.impressions >= SIGNAL_RULES.ctrImpressions && p.clicks / p.impressions < SIGNAL_RULES.ctrMax * 2);
      const moved = s.queries.filter(q => q.previousPosition !== null && Math.abs(q.previousPosition - q.position) >= 1).sort((a, b) => Math.abs((b.previousPosition ?? 0) - b.position) - Math.abs((a.previousPosition ?? 0) - a.position));
      return <>
            <section aria-label="Google Zoeken" className="rounded-lg border border-line bg-white p-5 sm:p-6" data-id="element-1935" data-mp="ao863t61yc3r">
              <dl className="grid grid-cols-2 gap-6 sm:grid-cols-3" data-id="element-1936" data-mp="fic9y3k0u40m">
                <Kpi size="lg" label="Klikken vanuit Google" value={s.clicks} data-id="element-1937" data-mp="sy2x23x3yoe" />
                <Kpi label="Keer getoond" value={s.impressions} data-id="element-1938" data-mp="5rhq15u8zgic" />
                <div className="col-span-2 text-[12px] leading-relaxed text-muted sm:col-span-1" data-id="element-1939" data-mp="3nw2xbphwl68q">
                  Google loopt {s.lagDays} dagen achter: de laatste dagen van de periode zijn nog niet compleet.
                </div>
              </dl>
            </section>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]" data-id="element-1940" data-mp="1yh0i964ar4nf">
              <ChartFrame title="Zoekwoorden" description="Waarop de site verscheen. Positie 1 = bovenaan." tableOnly fileName={`zoekwoorden-${period.from}-${period.to}`} table={{
            columns: [{
              label: 'Zoekwoord'
            }, {
              label: 'Getoond'
            }, {
              label: 'Klikken'
            }, {
              label: 'Klikratio',
              kind: 'pct'
            }, {
              label: 'Positie',
              kind: 'decimal'
            }],
            rows: s.queries.map(q => [q.query, q.impressions, q.clicks, ctr(q.clicks, q.impressions), q.position])
          }} data-id="element-1941" data-mp="1tmwawlr4cult" />

              <Panel title="Stijgers en dalers" description="Positie t.o.v. de vorige periode" data-id="element-1942" data-mp="23diiptslggty">
                {moved.length === 0 ? <p className="text-[13px] text-muted" data-id="element-1943" data-mp="ryvabgvkemfm">Geen grote verschuivingen.</p> : <ul className="divide-y divide-line text-[13px]" data-id="element-1944" data-mp="33mgvg71a8rns">
                    {moved.map(q => {
                const up = (q.previousPosition ?? q.position) > q.position;
                return <li key={q.query} className="flex items-baseline justify-between gap-3 py-2" data-id="element-1945" data-mp="3cmn8xswzdrmy">
                          <span className="min-w-0 truncate text-ink" data-id="element-1946" data-mp="3henblt4fqyn8">{q.query}</span>
                          <span className={`inline-flex shrink-0 items-center gap-1 tabular-nums ${up ? 'text-sage' : 'text-clay'}`} data-id="element-1947" data-mp="3mdht8rqo2h5r">
                            {up ? <ArrowUpIcon className="h-3 w-3" strokeWidth={2} aria-hidden="true" data-id="element-1948" data-mp="3r9yhdlxv8viu" /> : <ArrowDownIcon className="h-3 w-3" strokeWidth={2} aria-hidden="true" data-id="element-1949" data-mp="6jmg1euxpr1" />}
                            {up ? 'Gestegen' : 'Gedaald'} naar {fmt(q.position, 'decimal')}
                            <span className="text-muted" data-id="element-1950" data-mp="21xmssehpsi5s">(was {fmt(q.previousPosition, 'decimal')})</span>
                          </span>
                        </li>;
              })}
                  </ul>}
              </Panel>
            </div>

            <Panel title="Veel getoond, weinig geklikt" description="Een duidelijkere titel en beschrijving in Google levert hier het meest op." data-id="element-1951" data-mp="16n80wqf65wwe">
              {weak.length === 0 ? <p className="text-[13px] text-muted" data-id="element-1952" data-mp="1fnmm7ps4vbzw">Geen pagina’s die opvallen.</p> : <ul className="divide-y divide-line text-[13px]" data-id="element-1953" data-mp="rx92cmjpcjge">
                  {weak.map(p => <li key={p.path} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5" data-id="element-1954" data-mp="mz7hbw2pua2a">
                      <span className="min-w-0" data-id="element-1955" data-mp="11on5d4av41ea">
                        <span className="text-ink" data-id="element-1956" data-mp="wwvfyx3g01pq">{p.title}</span> <span className="text-muted" data-id="element-1957" data-mp="wvu2klyyf34u">{p.path}</span>
                      </span>
                      <span className="flex items-baseline gap-4" data-id="element-1958" data-mp="969753lm6deo">
                        <span className="tabular-nums text-muted" data-id="element-1959" data-mp="48hgse0mazcl">
                          {fmt(p.impressions)}× getoond · {fmt(p.clicks)} klikken
                        </span>
                        {p.editTo && <Link to={`${ADMIN_BASE}${p.editTo}`} className="text-ink underline underline-offset-4 hover:text-clay" data-id="element-1960" data-mp="2kmjgwspt0y98">
                            Verbeter titel
                          </Link>}
                      </span>
                    </li>)}
                </ul>}
            </Panel>
          </>;
    }}
    </ReportView>;
}